import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy, Download, FolderCog, KeyRound, MonitorCog, RefreshCw, Trash2 } from 'lucide-react';
import { Link } from 'react-router';
import { useState } from 'react';
import { API, del, get, qs } from '../api';
import { usePermissions } from '../auth';
import { Confirm, Empty, ErrorBox, Loading, PageHeader, useToast } from '../components/ui';
import { fmtBytes, fmtDate, fmtNumber, fmtRelative } from '../format';
import { AgentFoldersDrawer } from './AgentFolders';
import { useGroups } from './shared';

interface AgentFolder {
  name: string;
  enabled: boolean;
  pending: number;
  lastError: string | null;
  /** Definida desde este panel, no en el equipo. */
  managed?: boolean;
}

interface AgentRow {
  id: number;
  groupId: number;
  groupName: string;
  apiKey: { id: number; name: string; revoked: boolean } | null;
  agentId: string;
  name: string;
  hostname: string | null;
  os: string | null;
  version: string | null;
  ip: string | null;
  online: boolean;
  folders: AgentFolder[];
  stats: { processed?: number; failed?: number; pending?: number };
  /** Estado de la configuración remota de este equipo. */
  managed: {
    roots: string[];
    remoteBrowse: boolean;
    revision: number;
    appliedRevision: number;
    pending: boolean;
    error: string | null;
    folderCount: number;
  };
  startedAt: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
}

interface AgentPackage {
  file: string;
  version: string;
  os: 'win' | 'linux' | 'darwin';
  arch: string;
  format: 'zip' | 'tar.gz' | 'deb' | 'pkg';
  sizeBytes: number;
  sha256: string;
  publishedAt: string;
}

interface Step {
  text: string;
  command?: string;
}

/** Los dos últimos pasos son iguales en todos los sistemas. */
const ACTIVATE_STEPS: Step[] = [
  { text: 'En ese equipo abre http://127.0.0.1:8765 y entra con tu email y contraseña de este panel: el equipo se activa solo' },
  { text: 'Añade la carpeta donde escanea el escáner y la carpeta de destino' },
];

const OS_TITLE: Record<AgentPackage['os'], string> = { win: 'Windows', linux: 'Linux', darwin: 'macOS' };

const ARCH_LABEL = (p: AgentPackage) =>
  p.os === 'darwin' ? (p.arch === 'arm64' ? 'Apple Silicon' : 'Intel') : p.arch;

/** Instrucciones de instalación, con el nombre real del paquete que se descarga. */
function stepsFor(p: AgentPackage): Step[] {
  const file = p.file;
  switch (`${p.os}-${p.format}`) {
    case 'win-zip':
      return [
        { text: 'Descomprime el ZIP en una carpeta fija, p. ej. C:\\Program Files\\OCR1SK Agent' },
        { text: 'En una consola como Administrador, dentro de esa carpeta:', command: 'ocr1sk-agent.exe install-service' },
        ...ACTIVATE_STEPS,
      ];
    case 'linux-deb':
      return [
        { text: 'Instala el paquete (deja el servicio arrancado):', command: `sudo apt install ./${file}` },
        ...ACTIVATE_STEPS,
      ];
    // Otras distribuciones, sin dpkg.
    case 'linux-tar.gz':
      return [
        { text: 'Descomprime el paquete:', command: `sudo mkdir -p /opt/ocr1sk-agent && sudo tar -xzf ${file} -C /opt/ocr1sk-agent --strip-components=1` },
        { text: 'Instálalo como servicio:', command: 'sudo /opt/ocr1sk-agent/ocr1sk-agent install-service' },
        ...ACTIVATE_STEPS,
      ];
    case 'darwin-pkg':
      return [
        { text: 'Instálalo desde la consola (el instalador no está firmado por Apple):', command: `sudo installer -pkg ${file} -target /` },
        {
          text: 'Solo si los escaneos llegan a una unidad de red que monta el usuario, o a su Escritorio o Documentos: cámbialo a servicio de inicio de sesión, porque el del sistema corre como root y no las ve',
          command: 'sudo ocr1sk-agent uninstall-service && ocr1sk-agent install-service --at-login',
        },
        ...ACTIVATE_STEPS,
      ];
    case 'darwin-tar.gz': {
      const dir = `/usr/local/libexec/${file.replace(/\.tar\.gz$/, '')}`;
      return [
        { text: 'Descomprime el paquete y quítale la marca de cuarentena:', command: `sudo mkdir -p /usr/local/libexec && sudo tar -xzf ${file} -C /usr/local/libexec && sudo xattr -dr com.apple.quarantine ${dir}` },
        { text: 'Instálalo como servicio del sistema:', command: `sudo ${dir}/ocr1sk-agent install-service` },
        ...ACTIVATE_STEPS,
      ];
    }
    default:
      return ACTIVATE_STEPS;
  }
}

function CopyHash({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="btn btn-sm"
      title={`SHA-256: ${value}`}
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />} SHA-256
    </button>
  );
}

function DownloadsCard() {
  const packages = useQuery({
    queryKey: ['agent-downloads'],
    queryFn: async () => (await get<{ items: AgentPackage[] }>('/downloads/agent')).items,
    staleTime: 5 * 60_000,
  });

  return (
    <div className="card card-pad" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div className="card-title" style={{ marginBottom: 4 }}>
          Descargar el agente de carpetas
        </div>
        <Link to="/api-keys" className="btn btn-sm">
          <KeyRound size={14} /> Crear API key para el agente
        </Link>
      </div>
      <p className="muted" style={{ margin: '0 0 14px' }}>
        Se instala en el equipo que recibe los escaneos: vigila la carpeta, envía cada documento y deja el PDF con texto en la carpeta de destino.
      </p>
      <ErrorBox error={packages.error} />
      {packages.isLoading ? (
        <Loading />
      ) : !packages.data?.length ? (
        <div className="alert alert-warn">No hay instaladores publicados en el servidor. Genera los paquetes y cópialos a la carpeta de descargas (AGENT_DOWNLOADS_DIR).</div>
      ) : (
        <div className="downloads">
          {packages.data.map((p) => {
            const steps = stepsFor(p);
              return (
                <div key={p.file} className="download">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <div>
                    <div style={{ fontWeight: 650, fontSize: 15 }}>
                      {OS_TITLE[p.os]} <span className="cell-sub">{ARCH_LABEL(p)}</span>
                      {(p.format === 'deb' || p.format === 'pkg') && <span className="badge badge-accent" style={{ marginLeft: 8 }}>Recomendado</span>}
                      {p.format === 'tar.gz' && <span className="cell-sub"> · {p.os === 'darwin' ? 'instalación manual' : 'otras distribuciones'}</span>}
                    </div>
                    <div className="cell-sub">
                      Versión {p.version} · {fmtBytes(p.sizeBytes)} · {fmtDate(p.publishedAt)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <CopyHash value={p.sha256} />
                    <a className="btn btn-primary btn-sm" href={`${API}/downloads/agent/${encodeURIComponent(p.file)}`}>
                      <Download size={14} /> Descargar
                    </a>
                  </div>
                </div>
                {steps.length > 0 && (
                  <ol className="steps">
                    {steps.map((step) => (
                      <li key={step.text}>
                        {step.text}
                        {step.command && (
                          <div>
                            <code>{step.command}</code>
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const osLabel = (os: string | null) => (os?.startsWith('win32') ? 'Windows' : os?.startsWith('linux') ? 'Linux' : os?.startsWith('darwin') ? 'macOS' : os ?? '—');

export function AgentsPage() {
  const perms = usePermissions();
  const groups = useGroups();
  const qc = useQueryClient();
  const toast = useToast();
  const [groupId, setGroupId] = useState('');
  const [removing, setRemoving] = useState<AgentRow | null>(null);
  const [configuring, setConfiguring] = useState<AgentRow | null>(null);

  const agents = useQuery({
    queryKey: ['agents', { groupId }],
    queryFn: async () => (await get<{ items: AgentRow[] }>(`/agents${qs({ groupId })}`)).items,
    // Los agentes informan cada minuto.
    refetchInterval: 30_000,
  });

  return (
    <>
      <PageHeader
        title="Agentes"
        subtitle="Equipos con el agente de carpetas instalado. Informan cada minuto de su estado."
      />
      <DownloadsCard />
      <div className="card">
        {perms.isSuper && (
          <div className="toolbar">
            <select className="select" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">Todos los grupos</option>
              {groups.data?.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <ErrorBox error={agents.error} />
        {agents.isLoading ? (
          <Loading />
        ) : !agents.data?.length ? (
          <Empty icon={MonitorCog} title="Ningún agente conectado todavía">
            Instala el agente en el equipo que recibe los escaneos y configúralo con una API key.
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Agente</th>
                  <th>Estado</th>
                  <th>Carpetas</th>
                  <th className="num">Procesados</th>
                  <th>API key</th>
                  <th>Versión</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {agents.data.map((a) => {
                  const withErrors = a.folders.filter((f) => f.lastError);
                  return (
                    <tr key={a.id}>
                      <td>
                        <div className="cell-main">{a.name}</div>
                        <div className="cell-sub">
                          {[a.hostname, osLabel(a.os), a.ip].filter(Boolean).join(' · ')}
                          {perms.isSuper && ` · ${a.groupName}`}
                        </div>
                      </td>
                      <td>
                        {a.online ? <span className="badge badge-done">Conectado</span> : <span className="badge badge-cancelled">Desconectado</span>}
                        <div className="cell-sub" title={fmtDate(a.lastSeenAt)}>
                          {fmtRelative(a.lastSeenAt)}
                        </div>
                      </td>
                      <td className="wrap">
                        {a.folders.length === 0 ? (
                          <span className="faint">Sin carpetas</span>
                        ) : (
                          a.folders.map((f) => (
                            <div key={f.name} title={f.lastError ?? undefined}>
                              {f.lastError && <AlertTriangle size={13} style={{ color: 'var(--danger)', verticalAlign: -2, marginRight: 4 }} />}
                              <span className={f.enabled ? '' : 'faint'}>{f.name}</span>
                              {f.managed && <FolderCog size={12} className="faint" style={{ verticalAlign: -1, marginLeft: 4 }} aria-label="Configurada desde el panel" />}
                              {f.pending > 0 && <span className="cell-sub"> · {f.pending} en curso</span>}
                            </div>
                          ))
                        )}
                        {withErrors.length > 0 && <div className="cell-sub" style={{ color: 'var(--danger)' }}>{withErrors[0].lastError}</div>}
                        {a.managed.pending && (
                          <div className="cell-sub" title="El equipo todavía no ha aplicado el último cambio">
                            <RefreshCw size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                            Cambio pendiente
                          </div>
                        )}
                        {a.managed.error && <div className="cell-sub" style={{ color: 'var(--danger)' }}>{a.managed.error}</div>}
                      </td>
                      <td className="num">
                        {fmtNumber(a.stats.processed ?? 0)}
                        {!!a.stats.failed && <div className="cell-sub" style={{ color: 'var(--danger)' }}>{fmtNumber(a.stats.failed)} con error</div>}
                      </td>
                      <td>
                        {a.apiKey ? (
                          <>
                            {a.apiKey.name}
                            {a.apiKey.revoked && <div className="cell-sub" style={{ color: 'var(--danger)' }}>Revocada</div>}
                          </>
                        ) : (
                          <span className="faint">—</span>
                        )}
                      </td>
                      <td>{a.version ?? '—'}</td>
                      <td>
                        <div className="row-actions">
                          <button
                            className="btn btn-icon"
                            onClick={() => setConfiguring(a)}
                            aria-label="Configurar carpetas"
                            title={a.managed.roots.length ? 'Configurar carpetas de este equipo' : 'Configuración remota no habilitada en el equipo'}
                          >
                            <FolderCog size={16} />
                          </button>
                          {!a.online && (
                            <button className="btn btn-icon danger" onClick={() => setRemoving(a)} aria-label="Quitar de la lista" title="Quitar de la lista">
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {configuring && (
        <AgentFoldersDrawer agentId={configuring.id} agentName={configuring.name} onClose={() => setConfiguring(null)} />
      )}

      {removing && (
        <Confirm
          title="Quitar agente"
          message={
            <>
              ¿Quitar <strong>{removing.name}</strong> de la lista? Si el equipo vuelve a conectarse, reaparecerá. Para impedir
              que envíe documentos, revoca su API key.
            </>
          }
          confirmLabel="Quitar"
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            await del(`/agents/${removing.id}`);
            await qc.invalidateQueries({ queryKey: ['agents'] });
            toast('Agente quitado de la lista');
          }}
        />
      )}
    </>
  );
}
